// 261010 UI-quick lượt 6 — đầu mục bước: bỏ trùng với nút chính ở hàng trên cùng. THUẦN (không antd/router).
import type { WorkflowStepKey } from '../../types/filmWorkflow';
import type { PrimaryCall, SecondaryAction } from './workflowPrimaryPolicy';

type PrimaryLike = { call: PrimaryCall | null; reason?: string; waiting?: boolean; secondary?: SecondaryAction[] } | null | undefined;

/** Nút chính ở đầu trang đã nói thẳng «đang chạy … (Dừng)» ⇒ thân bước không lặp lại trạng thái chạy bằng một dòng chữ nữa. */
export const headerShowsRunOf = (primary: PrimaryLike): boolean => primary?.call?.type === 'STOP';

/** Dòng chờ «AI đang chấm/sửa…» ở thân bước có thừa không: thừa khi nút chính đang hiện «đang chạy (Dừng)» hoặc đang hiện chính câu chờ đó (nút Duyệt khoá). */
export const qualityWaitRedundantOf = (waitText: string | null | undefined, primary: PrimaryLike): boolean =>
  headerShowsRunOf(primary) || (!!primary?.waiting && !!waitText && primary.reason === waitText);

/** «Cùng một hành động trên cùng một bước». null ⇒ không có danh tính (không bao giờ coi là trùng). */
export function callIdentityOf(c: PrimaryCall | null | undefined): string | null {
  if (!c) return null;
  switch (c.type) {
    case 'STOP': return 'STOP';
    case 'RUN_STAGE1': return `RUN_STAGE1:${c.rerun ? 'rerun' : 'run'}`;
    case 'APPROVE': return `APPROVE:${c.step}:${c.runNext ? 'next' : 'only'}`;
    case 'RUN_STEP': return `RUN_STEP:${c.step}:${c.scope}`;
    case 'OPEN_STEP': return `OPEN_STEP:${c.step}`;
    case 'DOWNLOAD_CAPCUT': return 'DOWNLOAD_CAPCUT';
    default: return null;
  }
}

/** Bước mà nút chính đang nhắm tới (null nếu không gắn bước, vd Dừng). */
export function primaryStepOf(c: PrimaryCall | null | undefined): WorkflowStepKey | null {
  if (!c) return null;
  if (c.type === 'RUN_STAGE1') return 'SCRIPT';
  return 'step' in c ? (c.step as WorkflowStepKey) : null;
}

/** Nút của đầu mục bước sau khi bỏ phần thừa: (1) trùng hành động của nút chính / link phụ của nó ⇒ ẩn; (2) «Duyệt» không bấm được ⇒ ẩn (lý do đã ở đầu trang / dòng gợi ý), không để nút tắt. Bước KHÔNG phải bước hiện hành của plan: nút chạy / chạy lại / duyệt của nó còn nguyên (vì không trùng nút chính). */
export function stepHeadActionsOf(actions: SecondaryAction[], primary: PrimaryLike): SecondaryAction[] {
  const taken = new Set<string>();
  for (const c of [primary?.call, ...(primary?.secondary ?? []).map((s) => s.call)]) { const id = callIdentityOf(c); if (id) taken.add(id); }
  return actions.filter((a) => {
    if (a.key === 'APPROVE' && !a.enabled) return false;
    const id = callIdentityOf(a.call);
    return !(id && taken.has(id));
  });
}

/** Dòng gợi ý lý do nút tắt. Ẩn khi nút chính đã nhắm đúng bước này (nó đã nói việc cần làm) hoặc đã mang đúng lý do đó; giữ cho blocker mà nút chính không giải thích (xem bước sau khi bước trước chưa xong, thiếu ảnh, đang bận, dữ liệu cũ…). Lý do lấy từ TẤT CẢ nút (kể cả nút bị ẩn). */
export function stepHintOf(step: WorkflowStepKey, actions: SecondaryAction[], primary: PrimaryLike): string | null {
  const reason = actions.find((a) => !a.enabled && a.reason)?.reason;
  if (!reason) return null;
  if (primary?.call && primaryStepOf(primary.call) === step) return null;
  if (headerShowsRunOf(primary) && /^Đang (?:chạy|có bước khác chạy)/.test(reason)) return null; // nút chính đã là «Đang chạy … (Dừng)»
  if (primary?.reason && primary.reason === reason) return null;
  return reason;
}

/** Nút đầu mục nào được vẽ NỔI BẬT (chữ + màu): nút bấm được đứng cuối — trừ «Chạy lại Phân đoạn» (ghi đè 15 video + sửa tay) luôn là nút biểu tượng, không bao giờ thành nút nổi bật. */
export function headActionProminentOf(actions: SecondaryAction[], i: number): boolean {
  const a = actions[i];
  return !!a && a.enabled && i === actions.length - 1 && a.key !== 'RERUN_STAGE1';
}
